<script setup lang="ts">
/**
 * Picker de client existant — recherche serveur (débouncée) sur /companies/list,
 * auto-remplit raison sociale + SIREN. Lie la demande à un uuid client.
 * Combobox custom DaisyUI (pas de lib externe, cf MASTER).
 */
import { nextTick, ref, watch } from 'vue'
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  BuildingOffice2Icon,
  ArrowPathIcon,
} from '@heroicons/vue/24/outline'
import { getClientDetails, listClients, type ClientDetails, type ClientListItem } from '@/api/clientsApi'
import type { AccompClientRef } from '@/v2/accompagnement/api/accompagnementTypes'

const props = defineProps<{ modelValue: AccompClientRef | null; id?: string; invalid?: boolean }>()
const emit = defineEmits<{ 'update:modelValue': [AccompClientRef | null] }>()

const open = ref(false)
const query = ref('')
const results = ref<ClientListItem[]>([])
const loading = ref(false)
const enriching = ref(false)
const activeIndex = ref(-1)
const root = ref<HTMLElement | null>(null)
const inputEl = ref<HTMLInputElement | null>(null)

let debounce: ReturnType<typeof setTimeout> | null = null
let abort: AbortController | null = null

async function search() {
  const term = query.value.trim()
  abort?.abort()
  if (!term) {
    results.value = []
    activeIndex.value = -1
    loading.value = false
    return
  }
  abort = new AbortController()
  loading.value = true
  try {
    const res = await listClients({
      q: term,
      limit: 8,
      offset: 0,
      sort: 'name',
      order: 'asc',
      signal: abort.signal,
    })
    results.value = res.data
    activeIndex.value = res.data.length ? 0 : -1
    loading.value = false
  } catch (e: unknown) {
    if (e instanceof Error && e.name === 'AbortError') return
    results.value = []
    loading.value = false
  }
}

function onInput(e: Event) {
  query.value = (e.target as HTMLInputElement).value
  open.value = true
  if (debounce) clearTimeout(debounce)
  loading.value = !!query.value.trim()
  debounce = setTimeout(() => {
    debounce = null
    void search()
  }, 350)
}

function nafCodeOf(d: ClientDetails): { code: string | null; label: string | null } {
  if (!d.nafCode) return { code: null, label: null }
  if (typeof d.nafCode === 'string') return { code: d.nafCode, label: null }
  return { code: d.nafCode.code ?? null, label: d.nafCode.label ?? null }
}

function formatAddress(d: ClientDetails): string | null {
  const street = (d.street ?? d.metadata?.street ?? '').trim()
  const city = (d.city ?? d.metadata?.city ?? '').trim()
  const zip = (d.zip ?? '').trim()
  const parts = [street, `${zip} ${city}`.trim()].filter(Boolean)
  return parts.length ? parts.join(', ') : null
}

async function select(item: ClientListItem) {
  open.value = false
  query.value = ''
  results.value = []

  // Affichage immédiat depuis la liste ; le NAF / l'adresse arrivent après l'appel détail.
  const base: AccompClientRef = {
    uuid: item.uuid,
    name: item.name ?? '—',
    siren: item.siren,
    address: null,
    naf: null,
    nafLabel: null,
  }
  emit('update:modelValue', base)

  enriching.value = true
  try {
    const d = await getClientDetails(item.uuid)
    const naf = nafCodeOf(d)
    emit('update:modelValue', {
      uuid: item.uuid,
      name: item.name ?? d.name ?? '—',
      siren: item.siren,
      address: formatAddress(d),
      naf: naf.code,
      nafLabel: naf.label,
    })
  } catch {
    // Détail indisponible : on garde la fiche de base (champs NAF/adresse éditables).
  } finally {
    enriching.value = false
  }
}

function clearSelection() {
  emit('update:modelValue', null)
  query.value = ''
  results.value = []
  nextTick(() => inputEl.value?.focus())
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape') {
    open.value = false
    return
  }
  if (!results.value.length) return
  if (e.key === 'ArrowDown') {
    e.preventDefault()
    activeIndex.value = (activeIndex.value + 1) % results.value.length
  } else if (e.key === 'ArrowUp') {
    e.preventDefault()
    activeIndex.value = (activeIndex.value - 1 + results.value.length) % results.value.length
  } else if (e.key === 'Enter') {
    e.preventDefault()
    const item = results.value[activeIndex.value]
    if (item) select(item)
  }
}

function onClickOutside(e: MouseEvent) {
  if (root.value && !root.value.contains(e.target as Node)) open.value = false
}

watch(open, (v) => {
  if (v) document.addEventListener('mousedown', onClickOutside)
  else document.removeEventListener('mousedown', onClickOutside)
})
</script>

<template>
  <div ref="root" class="relative">
    <!-- Client sélectionné -->
    <div
      v-if="modelValue"
      class="flex items-center gap-3 rounded-lg border border-base-300 bg-base-200/60 px-3 py-2"
    >
      <BuildingOffice2Icon class="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
      <div class="min-w-0 flex-1">
        <p class="truncate font-medium text-base-content">{{ modelValue.name }}</p>
        <p v-if="modelValue.siren" class="text-sm tabular-nums text-base-content/70">
          SIREN {{ modelValue.siren }}
        </p>
        <p v-if="enriching" class="flex items-center gap-1 text-sm text-base-content/60">
          <ArrowPathIcon class="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
          Récupération de la fiche…
        </p>
      </div>
      <button
        type="button"
        class="btn btn-ghost btn-sm gap-1"
        @click="clearSelection"
      >
        <XMarkIcon class="h-4 w-4" aria-hidden="true" />
        Changer
      </button>
    </div>

    <!-- Recherche -->
    <template v-else>
      <div
        class="flex items-center gap-2 rounded-lg border bg-base-100 px-3 py-2 focus-within:ring-2 focus-within:ring-primary/20"
        :class="invalid ? 'border-error' : 'border-base-300 focus-within:border-primary'"
      >
        <MagnifyingGlassIcon
          class="h-5 w-5 shrink-0"
          :class="loading ? 'text-primary' : 'text-base-content/40'"
          aria-hidden="true"
        />
        <input
          :id="id"
          ref="inputEl"
          type="text"
          class="flex-1 bg-transparent text-base text-base-content outline-none placeholder:text-base-content/40"
          placeholder="Rechercher un client (nom, SIREN, SIRET)…"
          role="combobox"
          aria-expanded="true"
          aria-autocomplete="list"
          :value="query"
          @input="onInput"
          @focus="open = true"
          @keydown="onKeydown"
        />
        <ArrowPathIcon v-if="loading" class="h-4 w-4 animate-spin text-primary" aria-hidden="true" />
      </div>

      <ul
        v-if="open && query.trim()"
        role="listbox"
        class="absolute z-20 mt-1 max-h-72 w-full overflow-y-auto rounded-box border border-base-300 bg-base-100 p-1 shadow-lg"
      >
        <li v-if="loading && !results.length" class="px-3 py-2 text-sm text-base-content/70">
          Recherche…
        </li>
        <li v-else-if="!results.length" class="px-3 py-2 text-sm text-base-content/70">
          Aucun client pour « {{ query.trim() }} ».
        </li>
        <li v-for="(item, i) in results" v-else :key="item.uuid">
          <button
            type="button"
            role="option"
            :aria-selected="i === activeIndex"
            class="flex w-full flex-col items-start rounded-md px-3 py-2 text-left"
            :class="i === activeIndex ? 'bg-base-200' : 'hover:bg-base-200/60'"
            @mousemove="activeIndex = i"
            @click="select(item)"
          >
            <span class="font-medium text-base-content">{{ item.name ?? '—' }}</span>
            <span class="text-sm tabular-nums text-base-content/70">
              <template v-if="item.siren">SIREN {{ item.siren }}</template>
              <template v-if="item.siret"> · SIRET {{ item.siret }}</template>
              <template v-if="item.zip"> · {{ item.zip }}</template>
            </span>
          </button>
        </li>
      </ul>
    </template>
  </div>
</template>
